import {ref, watch} from "vue";
import {ogData} from "@/utils/ogTags.js";
import {annotate, cardTitle, groupLinkOf, makeEntry, parseTrail, pushView, stringifyTrail, TRAIL_KEY} from "@/utils/navTrail.js";

// The visitor's trail through this site, for the Back card. Browser only (sessionStorage); empty on the server,
// so the card renders nothing there and fills in on mount.
export const trail = ref([]);
export const trailReady = ref(false);

const store = () => { try { return window.sessionStorage; } catch { return null; } };

function save() {
    try { store()?.setItem(TRAIL_KEY, stringifyTrail(trail.value, window.location.hostname)); } catch { /* private mode */ }
}

function metaOf(pageData, path) {
    if (!pageData || !Array.isArray(pageData.sections) || pageData.sections.length === 0) return {};
    const og = ogData({pageData, origin: window.location.origin, path});
    return {title: cardTitle(og.title, og.siteName, path), description: og.description, image: og.image, imageAlt: og.alt};
}

let installed = false;

// Called once, from App.vue's onMounted.
export function installNavTrail(router, pageStore) {
    if (installed || typeof window === "undefined") return;
    installed = true;
    try {
        trail.value = parseTrail(store()?.getItem(TRAIL_KEY), window.location.hostname);
    } catch {
        trail.value = [];
    }

    let pop = "";
    router.options.history.listen((to, from, info) => { pop = info?.direction === "back" || info?.direction === "forward" ? info.direction : ""; });

    const record = (route, withMeta, step) => {
        const back = Array.isArray(route.query.back) ? route.query.back[0] : route.query.back;
        const entry = makeEntry({
            path: route.path,
            back,
            group: groupLinkOf(pageStore.navData, route.path),
            ...(withMeta ? metaOf(pageStore.pageData, route.path) : {}),
        });
        trail.value = pushView(trail.value, entry, step);
        save();
    };

    // A page loaded by the browser's back or forward button.
    const nav = typeof performance !== "undefined" && performance.getEntriesByType ? performance.getEntriesByType("navigation")[0] : null;
    record(router.currentRoute.value, true, nav?.type === "back_forward" ? "back" : "");
    trailReady.value = true;

    router.afterEach((to, from, failure) => {
        if (failure) return;
        const step = pop;
        pop = "";
        // The page data for the new route arrives a moment later (watch below).
        if (to.path !== from.path || !from.name) record(to, false, step);
    });

    // The new page's data lands after the route changes: add its title, photo and words to its entry.
    watch(() => pageStore.pageData, (data) => {
        const path = router.currentRoute.value.path;
        const meta = metaOf(data, path);
        if (!meta.title) return;
        trail.value = annotate(trail.value, path, {...meta, group: groupLinkOf(pageStore.navData, path)});
        save();
    });
}
